import { cn } from '../shared/utils/cn'
import { useAuth } from '../hooks/useAuth'

export type SettingsTab = 'units' | 'alerts' | 'cache' | 'charts' | 'preferences' | 'api-keys'

interface SettingsTabsProps {
  activeTab: SettingsTab
  onTabChange: (tab: SettingsTab) => void
}

const tabs = [
  { id: 'units' as const, name: 'Units' },
  { id: 'alerts' as const, name: 'Alerts' },
  { id: 'cache' as const, name: 'Cache' },
  { id: 'charts' as const, name: 'Charts' },
  { id: 'preferences' as const, name: 'Preferences' },
  { id: 'api-keys' as const, name: 'API Keys' },
]

export function SettingsTabs({ activeTab, onTabChange }: SettingsTabsProps) {
  const { isGuest, isAdmin } = useAuth()
  const guest = isGuest()
  const admin = isAdmin()

  // Both ends of the role range are refused a key, for opposite reasons.
  // Guests hold per-device grants from someone else and own nothing, so a key
  // of theirs would have no coherent reach; an admin's reach is the whole
  // fleet, which is not something to hand to a long-lived credential.
  const keysUnavailableReason = guest
    ? 'API keys are not available on guest accounts'
    : admin
      ? 'API keys are not available on admin accounts — use a non-admin service account for programmatic access'
      : null

  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8" aria-label="Settings Tabs">
        {tabs.map((tab) => {
          // The tab stays visible but disabled: one that vanishes for some
          // accounts reads as a bug, where a disabled one carrying a reason
          // reads as a decision.
          const disabled = tab.id === 'api-keys' && keysUnavailableReason !== null

          return (
            <button
              key={tab.id}
              onClick={() => !disabled && onTabChange(tab.id)}
              disabled={disabled}
              title={disabled ? keysUnavailableReason ?? undefined : undefined}
              className={cn(
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {tab.name}
            </button>
          )
        })}
      </nav>
    </div>
  )
}
